import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MapPin } from 'lucide-react';
import { restaurantInfo } from '../../data/mock';
import { useLanguage } from '../../context/LanguageContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { lang, toggleLang, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: t('nav', 'home') },
    { path: '/presentation', label: t('nav', 'restaurant') },
    { path: '/menu', label: t('nav', 'menu') },
    { path: '/histoire', label: t('nav', 'dishes') },
    { path: '/evenements', label: t('nav', 'events') },
    { path: '/reservation', label: t('nav', 'book') },
    { path: '/contact', label: t('nav', 'contact') },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-md'
          : 'bg-transparent'
      }`}
    >
      {/* Top bar */}
      <div className={`hidden md:block transition-all duration-300 ${isScrolled ? 'h-0 overflow-hidden' : 'h-auto'}`}>
        <div className="bg-[#8B4513] text-white py-2">
          <div className="max-w-7xl mx-auto px-6 flex justify-between items-center text-sm">
            <div className="flex items-center gap-6">
              <span className="flex items-center gap-2">
                <MapPin size={14} />
                {restaurantInfo.address}
              </span>
              <span className="flex items-center gap-2">
                <Phone size={14} />
                {restaurantInfo.phone}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span>{t('nav', 'topBarHours')}</span>
              <button
                onClick={toggleLang}
                className="ml-2 px-2.5 py-0.5 rounded-full border border-white/40 text-xs font-bold tracking-wider hover:bg-white/20 transition-colors"
                data-testid="lang-toggle"
                aria-label="Toggle language"
              >
                {lang === 'fr' ? 'EN' : 'FR'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src={restaurantInfo.logo}
              alt="Ya Habibi Logo"
              className="h-14 w-auto rounded-lg shadow-sm"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-medium transition-all duration-200 hover:text-[#D4A853] ${
                  location.pathname === link.path
                    ? 'text-[#D4A853] border-b-2 border-[#D4A853]'
                    : isScrolled
                    ? 'text-[#0f172a]'
                    : 'text-[#0f172a]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            {/* Language toggle for scrolled state */}
            {isScrolled && (
              <button
                onClick={toggleLang}
                className="px-3 py-1 rounded-full border-2 border-[#0f172a] text-xs font-bold tracking-wider hover:bg-[#0f172a] hover:text-white transition-all"
                data-testid="lang-toggle-scrolled"
              >
                {lang === 'fr' ? 'EN' : 'FR'}
              </button>
            )}
          </div>

          {/* CTA Button */}
          <Link
            to="/reservation"
            className="hidden lg:block bg-[#0f172a] text-white px-6 py-3 rounded-md font-semibold hover:bg-[#1e293b] transition-all duration-200 hover:-translate-y-0.5 shadow-md"
          >
            {t('nav', 'bookTable')}
          </Link>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-3">
            <button
              onClick={toggleLang}
              className="px-2.5 py-1 rounded-full border-2 border-[#0f172a] text-xs font-bold tracking-wider"
              data-testid="lang-toggle-mobile"
            >
              {lang === 'fr' ? 'EN' : 'FR'}
            </button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-md hover:bg-[#ECEC75]/50 transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div
          className={`lg:hidden absolute left-0 right-0 top-full bg-white shadow-lg transition-all duration-300 ${
            isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
        >
          <div className="px-6 py-4 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block py-2 font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'text-[#D4A853]'
                    : 'text-[#0f172a] hover:text-[#D4A853]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/reservation"
              onClick={() => setIsMenuOpen(false)}
              className="block w-full text-center bg-[#0f172a] text-white px-6 py-3 rounded-md font-semibold hover:bg-[#1e293b] transition-all"
            >
              {t('nav', 'bookTable')}
            </Link>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
